import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * A hook that returns a stable function reference that invokes the provided handler
 * with the latest props and state.
 *
 * This is useful for extracting logic from Effects that needs to access reactive values
 * but shouldn't trigger the Effect to re-run.
 *
 * @example
 * const onConnected = useEffectEvent((roomId) => {
 *   logConnection(roomId, theme);
 * });
 *
 * useEffect(() => {
 *   const connection = createConnection(serverUrl, roomId);
 *   connection.connect();
 *   onConnected(roomId);
 *   return () => connection.disconnect();
 * }, [roomId]); // theme is not a dependency!
 *
 * @param handler - The function to be wrapped.
 * @returns A stable function that delegates to the latest handler.
 *
 * FIXME: This is a replacement until we move to React 19.2.
 * The official `useEffectEvent` hook should be used once available.
 */
export const useEffectEvent = <Args extends unknown[], Return>(
  handler: (...args: Args) => Return,
): ((...args: Args) => Return) => {
  const handlerRef = useRef(handler);

  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  return useCallback((...args: Args) => {
    return handlerRef.current(...args);
  }, []);
};
